/**
 * Shared handling for binary (file) API responses.
 *
 * A labelled copy that arrives truncated or empty still carries
 * `Content-Type: application/pdf`, so the browser's PDF viewer only reports
 * "Failed to load PDF document" with nothing to act on. These helpers check the
 * body against the declared length and the PDF signature so the UI can show
 * what actually happened and keep the file name the server chose.
 */

const PDF_SIGNATURE = '%PDF-';
const SNIFF_BYTES = 1024;

function headerValue(response, name) {
  const headers = response?.headers;
  if (!headers || typeof headers.get !== 'function') return null;
  const value = headers.get(name);
  return typeof value === 'string' ? value : null;
}

function parseContentLength(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

export function formatBytes(size) {
  const value = Number(size) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

/** File name from a Content-Disposition header; '' when the header has none. */
export function fileNameFromDisposition(header) {
  const value = String(header || '');
  const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(value);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // fall through to the plain form
    }
  }
  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(value);
  if (quoted) return quoted[1].trim();
  const bare = /filename\s*=\s*([^;]+)/i.exec(value);
  return bare ? bare[1].trim() : '';
}

/** Extension that matches the MIME type, for downloads the server did not name. */
export function extensionForMime(mimeType) {
  const mime = String(mimeType || '').toLowerCase();
  if (mime === 'application/pdf') return '.pdf';
  if (mime === 'application/zip' || mime === 'application/x-zip-compressed') return '.zip';
  if (mime === 'application/json') return '.json';
  if (mime.startsWith('text/')) return '.txt';
  return '';
}

/** Message for a response that claims to be a PDF but does not start like one. */
export function unreadablePdfMessage(size) {
  return `The server returned a file of ${formatBytes(size)} that is not a readable PDF, so it cannot be shown here. Download it to inspect, or re-issue the copy.`;
}

/**
 * Read a successful file response. Throws when the body is empty or shorter
 * than the declared Content-Length, so callers never hand a broken file to the
 * viewer or the download manager.
 */
export async function readFileResponse(response) {
  const blob = await response.blob();
  const headerType = headerValue(response, 'content-type');
  const mimeType = (headerType || blob.type || 'application/octet-stream')
    .split(';')[0]
    .trim()
    .toLowerCase();
  const expected = parseContentLength(headerValue(response, 'content-length'));
  if (expected !== null && blob.size !== expected) {
    const error = new Error(
      `The file arrived incomplete (${formatBytes(blob.size)} of ${formatBytes(expected)}). Try again.`
    );
    error.code = 'INCOMPLETE_DOWNLOAD';
    throw error;
  }
  if (blob.size === 0) {
    const error = new Error('The server returned an empty file.');
    error.code = 'EMPTY_DOWNLOAD';
    throw error;
  }
  return {
    blob,
    mimeType,
    fileName: fileNameFromDisposition(headerValue(response, 'content-disposition')),
    size: blob.size,
  };
}

function readHead(blob) {
  const head = blob.slice(0, SNIFF_BYTES);
  if (typeof head.arrayBuffer === 'function') {
    return head.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  if (typeof FileReader === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => resolve(null);
    reader.readAsArrayBuffer(head);
  });
}

/**
 * True when the first bytes carry the PDF signature. Resolves true when the
 * runtime cannot read the blob, so an old browser never blocks a valid file.
 */
export async function looksLikePdf(blob) {
  if (!blob || typeof blob.slice !== 'function') return false;
  if (blob.size === 0) return false;
  const bytes = await readHead(blob);
  if (!bytes) return true;
  const text = String.fromCharCode(...bytes);
  return text.includes(PDF_SIGNATURE);
}

/** Download file name: the server's name first, otherwise label + MIME extension. */
export function downloadFileName({ fileName, label, fallback, mimeType }) {
  if (fileName) return fileName;
  const ext = extensionForMime(mimeType) || '.pdf';
  const safe = String(label || fallback || 'labelled-copy')
    .replace(/[\\/:*?"<>|]/g, '-')
    .trim();
  const base = safe || String(fallback || 'labelled-copy');
  return base.toLowerCase().endsWith(ext) ? base : `${base}${ext}`;
}
